import { createContext, useContext, ReactNode, useEffect, useState } from 'react';
import { supabase } from './supabaseClient';
import type { User } from '@supabase/supabase-js';

interface AuthContextType {
  currentUser: User | null;
  isLoadingAuth: boolean;
  isLoadingPublicSettings: boolean;
  authError: { type: string; message: string } | null;
  navigateToLogin: () => void;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [authError, setAuthError] = useState<{ type: string; message: string } | null>(null);

  useEffect(() => {
    // Check for existing session
    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          setAuthError({ type: 'auth_error', message: error.message });
        }
        setCurrentUser(session?.user ?? null);
      } catch (err) {
        setAuthError({ type: 'auth_error', message: 'Failed to check session' });
      } finally {
        setIsLoadingAuth(false);
      }
    };

    checkSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setCurrentUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const navigateToLogin = () => { window.location.href = '/login'; };
  
  const logout = async () => {
    await supabase.auth.signOut();
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={{
      currentUser,
      user: currentUser, // Alias for hook compatibility
      isLoadingAuth,
      isLoadingPublicSettings: false,
      authError,
      navigateToLogin,
      logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
